import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Paper,
  Chip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Pagination } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DateRangeCalendar from "screens/Customers/component/DateRangeCalendar";
import dayjs from "dayjs";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import CustomLabel from "components/CustomLabel";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/icon";
import { GetCustomer } from "screens/Customers/logic/GetCustomer";
import { GetService } from "screens/Services/logic/GetService";
import { GetServicesAndBundlesReport } from "../logic/ServicesAndBundlesReportLogic";
import ImportExportOptions from "components/ImportExportOptions";

// Helper function to format dates
const formatDateSubtractDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      count={pageCount}
      page={page + 1}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
    />
  );
}

const ServiceBundleReports = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState(null);
  const [conversionAccordionExpanded, setConversionAccordionExpanded] = useState(true);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [revenueDialogOpen, setRevenueDialogOpen] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [profitDialogOpen, setProfitDialogOpen] = useState(false);
  const [dialogServiceName, setDialogServiceName] = useState("");

  // Filter states
  const [selectedCustomerNames, setSelectedCustomerNames] = useState([]);
  const [selectedCustomerTypes, setSelectedCustomerTypes] = useState([]);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  
  // Filter options
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [customerTypeOptions, setCustomerTypeOptions] = useState([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  // API data states
  const [rawApiData, setRawApiData] = useState([]);
  const [aggregatedServicesData, setAggregatedServicesData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalServices: 0,
    totalRevenue: 0,
    totalCost: 0,
    avgProfitMargin: 0
  });
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(null);

  // Date range state
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));


  // Fetch filter options
  const fetchFilterOptions = async () => {
    setFiltersLoading(true);
    try {
      // Fetch Customer Names
      const customerResponse = await GetCustomer(0, navigate);
      // console.log('Customer Response:', customerResponse);
      if (customerResponse) {
        const customerOptions = customerResponse.map(customer => {
          const displayName = customer.customerType === "Business" 
            ? customer.companyName 
            : `${customer.firstName} ${customer.lastName}`;
          return {
            value: customer.customerID,
            label: displayName,
            customerType: customer.customerType
          };
        }).filter(option => option.label.trim() !== '') // Remove empty names
          .sort((a, b) => a.label.localeCompare(b.label));
        
        setCustomerNameOptions(customerOptions);
      }

      // Set Customer Type options
      setCustomerTypeOptions([
        { value: 'Business', label: 'Business' },
        { value: 'Individual', label: 'Individual' }
      ]);

      // Fetch Service Types from GetService
      const serviceResponse = await GetService(0, 100000, 1, '', navigate);
      // console.log('Service Response:', serviceResponse);
      if (serviceResponse && !serviceResponse.isError && serviceResponse.result) {
        const uniqueServices = [...new Set(serviceResponse.result.map(item => item.serviceOfferingName))]
          .filter(service => service) // Remove null/undefined
          .sort()
          .map(service => ({ value: service, label: service }));
        setServiceTypeOptions(uniqueServices);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchServicesAndBundlesData();
  }, [])

  // Data transformation functions
  const aggregateDataByService = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    const serviceMap = {};
    
    apiData.forEach(item => {
      const serviceName = item.service || 'Unknown Service';
      
      if (!serviceMap[serviceName]) {
        serviceMap[serviceName] = {
          id: serviceName,
          service: serviceName,
          totalUsage: 0,
          totalRevenue: 0,
          totalCost: 0,
          profit: 0,
          profitMargin: 0,
          invoiceCount: 0
        };
      }
      
      serviceMap[serviceName].totalUsage += item.jobCount || 0;
      serviceMap[serviceName].totalRevenue += item.totalRevenue || 0;
      serviceMap[serviceName].totalCost += item.totalCost || 0;
      serviceMap[serviceName].profit += item.totalProfit || 0;
      serviceMap[serviceName].invoiceCount += 1;
    });

    // Calculate profit margin for each service
    const aggregatedData = Object.values(serviceMap).map(service => ({
      ...service,
      profitMargin: service.totalRevenue > 0 
        ? Math.round((service.profit / service.totalRevenue) * 100) 
        : 0
    }));

    return aggregatedData;
  };

  const calculateSummaryMetrics = (apiData, aggregatedData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return { totalServices: 0, totalRevenue: 0, totalCost: 0, avgProfitMargin: 0 };
    }

    const totalServices = aggregatedData.length;
    const totalRevenue = apiData.reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
    const totalCost = apiData.reduce((sum, item) => sum + (item.totalCost || 0), 0);
    
    // Average profit margin across all services
    const avgProfitMargin = aggregatedData.length > 0
      ? Math.round(aggregatedData.reduce((sum, service) => sum + service.profitMargin, 0) / aggregatedData.length)
      : 0;

    return {
      totalServices,
      totalRevenue,
      totalCost,
      avgProfitMargin
    };
  };

  // Fetch services and bundles data
  const fetchServicesAndBundlesData = async () => {
    setDataLoading(true);
    setDataError(null);
    
    try {
      // Prepare filter parameters
      // Convert selected customer IDs to customer names
      const customerNames = selectedCustomerNames.length > 0 
        ? selectedCustomerNames.map(customerId => {
            const customer = customerNameOptions.find(c => c.value === customerId);
            return customer ? customer.label : '';
          }).filter(name => name !== '').join(',')
        : '';
      
      const customerTypes = selectedCustomerTypes.length > 0 ? selectedCustomerTypes.join(',') : '';
      const serviceTypes = selectedServiceTypes.length > 0 ? selectedServiceTypes.join(',') : '';
      
      console.log('Fetching data with filters:', {
        startDate,
        endDate,
        serviceTypes,
        customerNames,
        customerTypes        
      });

      const apiResponse = await GetServicesAndBundlesReport(
        startDate,
        endDate,
        serviceTypes,
        customerNames,
        customerTypes,        
        navigate
      );

      // console.log('API Response:', apiResponse);

      if (apiResponse && Array.isArray(apiResponse)) {
        setRawApiData(apiResponse);
        
        // Aggregate data by service
        const aggregated = aggregateDataByService(apiResponse);
        setAggregatedServicesData(aggregated);
        
        // Calculate summary metrics
        const metrics = calculateSummaryMetrics(apiResponse, aggregated);
        setSummaryMetrics(metrics);
        
        // console.log('Processed Data:', { aggregated, metrics });
      } else {
        console.warn('Invalid API response format');
        setRawApiData([]);
        setAggregatedServicesData([]);
        setSummaryMetrics({ totalServices: 0, totalRevenue: 0, totalCost: 0, avgProfitMargin: 0 });
      }
    } catch (error) {
      console.error('Error fetching services and bundles data:', error);
      setDataError('Failed to fetch data. Please try again.');
      setRawApiData([]);
      setAggregatedServicesData([]);
      setSummaryMetrics({ totalServices: 0, totalRevenue: 0, totalCost: 0, avgProfitMargin: 0 });
    } finally {
      setDataLoading(false);
    }
  };

  // Handle filter changes
  const handleCustomerNameChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedCustomerNames.length === customerNameOptions.length) {
        setSelectedCustomerNames([]);
      } else {
        setSelectedCustomerNames(customerNameOptions.map(option => option.value));
      }
    } else {
      setSelectedCustomerNames(value);
    }
  };

  const handleCustomerTypeChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedCustomerTypes.length === customerTypeOptions.length) {
        setSelectedCustomerTypes([]);
      } else {
        setSelectedCustomerTypes(customerTypeOptions.map(option => option.value));
      }
    } else {
      setSelectedCustomerTypes(value);
    }
  };

  const handleServiceTypeChange = (event) => {
    const value = event.target.value;
    
    if (value.includes('all')) {
      // If "All" is selected, select all options or deselect all
      if (selectedServiceTypes.length === serviceTypeOptions.length) {
        setSelectedServiceTypes([]);
      } else {
        setSelectedServiceTypes(serviceTypeOptions.map(option => option.value));
      }
    } else {
      setSelectedServiceTypes(value);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  // Handle run filter
  const handleRunFilter = () => {
    fetchServicesAndBundlesData();
  };

  // Get filtered data for dialogs
  const getFilteredDataForService = (serviceName) => {
    return rawApiData.filter(item => item.service === serviceName);
  };

  // Transform data for usage dialog
  const getUsageDialogData = (serviceName) => {
    const filteredData = getFilteredDataForService(serviceName);
    return filteredData.map((item, index) => ({
      id: index + 1,
      date: dayjs(item.invoiceDate).format('M/D/YY'),
      invoiceId: `INV-${item.invoiceID}`,
      customerName: item.customerName || 'N/A',
      service: item.service || 'N/A',
      qty: item.jobCount || 0,
      rate: `$${(item.serviceRate || 0).toFixed(2)}`,
      totalRevenue: `$${(item.totalRevenue || 0).toLocaleString()}`
    }));
  };

  // Transform data for revenue dialog
  const getRevenueDialogData = (serviceName) => {
    const filteredData = getFilteredDataForService(serviceName);
    return filteredData.map((item, index) => ({
      id: index + 1,
      date: dayjs(item.invoiceDate).format('M/D/YY'),
      invoiceId: `INV-${item.invoiceID}`,
      customerName: item.customerName || 'N/A',
      service: item.service || 'N/A',
      qty: item.jobCount || 0,
      rate: `$${(item.serviceRate || 0).toFixed(2)}`,
      lineRevenue: `$${(item.totalRevenue || 0).toLocaleString()}`
    }));
  };

  // Transform data for cost dialog
  const getCostDialogData = (serviceName) => {
    const filteredData = getFilteredDataForService(serviceName);
    return filteredData.map((item, index) => ({
      id: index + 1,
      date: dayjs(item.invoiceDate).format('M/D/YY'),
      invoiceId: `INV-${item.invoiceID}`,
      laborCost: `$${(item.laborCost || 0).toLocaleString()}`,
      materialCost: `$${(item.materialCost || 0).toLocaleString()}`,
      assetCost: `$${(item.assetCost || 0).toLocaleString()}`,
      otherCost: `$${(item.otherCost || 0).toLocaleString()}`,
      totalCost: `$${(item.totalCost || 0).toLocaleString()}`
    }));
  };

  // Transform data for profit dialog
  const getProfitDialogData = (serviceName) => {
    const filteredData = getFilteredDataForService(serviceName);
    return filteredData.map((item, index) => ({
      id: index + 1,
      date: dayjs(item.invoiceDate).format('M/D/YY'),
      invoiceId: `INV-${item.invoiceID}`,
      revenue: `$${(item.totalRevenue || 0).toLocaleString()}`,
      cost: `$${(item.totalCost || 0).toLocaleString()}`,
      profit: `$${(item.totalProfit || 0).toLocaleString()}`
    }));
  };

  // // Sample data for services
  // const servicesData = [
  //   {
  //     id: 1,
  //     service: "Plumbing Service",
  //     totalUsage: 11,
  //     totalRevenue: 948.55,
  //     totalCost: 569.13,
  //     profit: 379.42,
  //     profitMargin: 40,
  //   },
  //   {
  //     id: 2,
  //     service: "Electrical Service",
  //     totalUsage: 12,
  //     totalRevenue: 1250.0,
  //     totalCost: 837.5,
  //     profit: 412.5,
  //     profitMargin: 33,
  //   },
  //   {
  //     id: 3,
  //     service: "HVAC Maintenance",
  //     totalUsage: 14,
  //     totalRevenue: 2100.0,
  //     totalCost: 1785.0,
  //     profit: 315.0,
  //     profitMargin: 15,
  //   },
  //   {
  //     id: 4,
  //     service: "Drain Cleaning",
  //     totalUsage: 20,
  //     totalRevenue: 105.55,
  //     totalCost: 79.16,
  //     profit: 26.39,
  //     profitMargin: 25,
  //   },
  // ];

  // // Sample data for usage dialog
  // const usageData = [
  //   { 
  //     id: 1, 
  //     date: "9/18/25", 
  //     invoiceId: "INV-2001", 
  //     customerName: "John Smith", 
  //     service: "Plumbing Service", 
  //     qty: 2, 
  //     rate: "$948.55", 
  //     totalRevenue: "$2,000" 
  //   },
  //   { 
  //     id: 2, 
  //     date: "8/15/25", 
  //     invoiceId: "INV-2005", 
  //     customerName: "Brooklyn Simmons", 
  //     service: "Plumbing Service", 
  //     qty: 1, 
  //     rate: "$105.55", 
  //     totalRevenue: "$1,200" 
  //   },
  // ];

  // // Sample data for revenue dialog
  // const revenueData = [
  //   { 
  //     id: 1, 
  //     date: "8/18/25", 
  //     invoiceId: "NV-2001", 
  //     customerName: "John Smith", 
  //     service: "Plumbing Service", 
  //     qty: 2, 
  //     rate: "$3946.55", 
  //     lineRevenue: "$2,000" 
  //   },
  //   { 
  //     id: 2, 
  //     date: "8/15/25", 
  //     invoiceId: "NV-2005", 
  //     customerName: "Brooklyn Simmons", 
  //     service: "Plumbing Service", 
  //     qty: 1, 
  //     rate: "$105.55", 
  //     lineRevenue: "$1,200" 
  //   },
  // ];

  // // Sample data for cost dialog
  // const costData = [
  //   { 
  //     id: 1, 
  //     date: "9/18/25", 
  //     invoiceId: "INV-2001", 
  //     laborCost: "$948.55", 
  //     materialCost: "$948.55", 
  //     totalCost: "$2,000" 
  //   },
  //   { 
  //     id: 2, 
  //     date: "8/15/25", 
  //     invoiceId: "INV-2005", 
  //     laborCost: "$105.55", 
  //     materialCost: "$105.55", 
  //     totalCost: "$1,200" 
  //   },
  // ];

  // // Sample data for profit dialog
  // const profitData = [
  //   { 
  //     id: 1, 
  //     date: "9/18/25", 
  //     invoiceId: "INV-2001", 
  //     revenue: "$948.55", 
  //     cost: "$948.55", 
  //     profit: "$2,000" 
  //   },
  //   { 
  //     id: 2, 
  //     date: "8/15/25", 
  //     invoiceId: "INV-2005", 
  //     revenue: "$105.55", 
  //     cost: "$105.55", 
  //     profit: "$1,200" 
  //   },
  // ];

  // Columns for usage dialog DataGrid
  const usageColumns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "invoiceId", headerName: "Invoice/WO ID", flex: 1 },
    { field: "customerName", headerName: "Customer Name", flex: 1 },
    { field: "service", headerName: "Service", flex: 1 },
    { field: "qty", headerName: "Qty", flex: 1 },
    { field: "rate", headerName: "Rate", flex: 1 },
    { field: "totalRevenue", headerName: "Total Revenue", flex: 1 },
  ];

  // Columns for revenue dialog DataGrid
  const revenueColumns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "invoiceId", headerName: "Invoice/WO ID", flex: 1 },
    { field: "customerName", headerName: "Customer Name", flex: 1 },
    { field: "service", headerName: "Service", flex: 1 },
    { field: "qty", headerName: "Qty", flex: 1 },
    { field: "rate", headerName: "Rate", flex: 1 },
    { field: "lineRevenue", headerName: "Line Revenue", flex: 1 },
  ];

  // Columns for cost dialog DataGrid
  const costColumns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "invoiceId", headerName: "Invoice/WO ID", flex: 1 },
    { field: "laborCost", headerName: "Labor Cost", flex: 1 },
    { field: "materialCost", headerName: "Material Cost", flex: 1 },
    { field: "assetCost", headerName: "Asset Cost", flex: 1 },
    { field: "otherCost", headerName: "Other Cost", flex: 1 },
    { field: "totalCost", headerName: "Total Cost", flex: 1 },
  ];

  // Columns for profit dialog DataGrid
  const profitColumns = [
    { field: "date", headerName: "Date", flex: 1 },
    { field: "invoiceId", headerName: "Invoice/WO ID", flex: 1 },
    { field: "revenue", headerName: "Revenue", flex: 1 },
    { field: "cost", headerName: "Cost", flex: 1 },
    { field: "profit", headerName: "Profit", flex: 1 },
  ];

  const handleUsageClick = (serviceName) => {
    setDialogServiceName(serviceName);
    setUsageDialogOpen(true);
  };

  const handleRevenueClick = (serviceName) => {
    setDialogServiceName(serviceName);
    setRevenueDialogOpen(true);
  };

  const handleCostClick = (serviceName) => {
    setDialogServiceName(serviceName);
    setCostDialogOpen(true);
  };

  const handleProfitClick = (serviceName) => {
    setDialogServiceName(serviceName);
    setProfitDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setUsageDialogOpen(false);
    setRevenueDialogOpen(false);
    setCostDialogOpen(false);
    setProfitDialogOpen(false);
    setDialogServiceName("");
  };

  const columns = [
    {
      field: "service",
      headerName: "Service / Bundle",
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
          onClick={() => {
            setSelectedService(params.row);
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { 
      field: "totalUsage", 
      headerName: "Total Usage", 
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            textDecoration: "underline",
            color: "primary.main",
            "&:hover": {
              color: "primary.dark",
            },
          }}
          onClick={() => handleUsageClick(params.row.service)}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "totalRevenue",
      headerName: "Total Revenue",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            textDecoration: "underline",
            color: "primary.main",
            "&:hover": {
              color: "primary.dark",
            },
          }}
          onClick={() => handleRevenueClick(params.row.service)}
        >
          ${params.value.toLocaleString()}
        </Box>
      ),
    },
    {
      field: "totalCost",
      headerName: "Total Cost",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            textDecoration: "underline",
            color: "primary.main",
            "&:hover": {
              color: "primary.dark",
            },
          }}
          onClick={() => handleCostClick(params.row.service)}
        >
          ${params.value.toLocaleString()}
        </Box>
      ),
    },
    {
      field: "profit",
      headerName: "Profit",
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            textDecoration: "underline",
            color: "primary.main",
            "&:hover": {
              color: "primary.dark",
            },
          }}
          onClick={() => handleProfitClick(params.row.service)}
        >
          {params.value < 0 ? `-$${Math.abs(params.value).toLocaleString()}` : `$${params.value.toLocaleString()}`}
        </Box>
      ),
    },
   {
  field: "profitMargin",
  headerName: "Profit Margin",
  flex: 1,
  renderCell: (params) => (
    <Typography
      variant="body2"
      sx={{
        color:
          params.value >= 30
            ? "success.main"
            : params.value >= 20
            ? "warning.main"
            : "error.main",
        fontWeight: "bold",
       mt:4,
      }}
    >
      {params.value.toLocaleString()}%
    </Typography>
  ),
},
  ];

  const [dateRange, setDateRange] = React.useState([
    dayjs(formatDateSubtractDays(new Date(), 90)),
    dayjs(formatDateSubtractDays(new Date(), 0)),
  ]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (option) => {
    setAnchorEl(null);
    if (option) {
      console.log("Export as:", option);
      // Export logic here
    }
  };

  const handleNavigation = (to) => {
    navigate(to);
  };

  const handlePageBack = () => {
    handleNavigation("/reports");
  };

  const handleConversionAccordionChange = () => {
    setConversionAccordionExpanded(!conversionAccordionExpanded);
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Service & Bundle Reports"
    >
      <Card
        sx={{
          p: 3,
          boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
          height: "100%",
          width: "100%",
          overflowX: "auto",
          mx: "auto",
          mt: 4,
        }}
      >
        <CardContent>
          <Box
            sx={{
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              p: 3,
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h4">Service & Bundle Reports</Typography>
                <Typography variant="body1" color="text.secondary">
                  View and export Service & Bundle Reports
                </Typography>
              </Box>
              <ImportExportOptions 
                handleOpenBulkImportDlg={()=>{}}                          
                data={aggregatedServicesData}
                module="ServicesAndBundles"
              />  

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose(null)}
              >
                <MenuItem onClick={() => handleClose("csv")}>CSV</MenuItem>
                <MenuItem onClick={() => handleClose("xls")}>XLS</MenuItem>
                <MenuItem onClick={() => handleClose("pdf")}>PDF</MenuItem>
              </Menu>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC", mt: 2 }}
              >
                <Typography variant="h5">Filters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    gap: 3,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Date Range */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                     <CustomLabel label="Date Range"  />
                    <DateRangeCalendar
                      initialStartDate={dayjs(startDate)}
                      initialEndDate={dayjs(endDate)}
                      onDateChange={handleDateRangeChange}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        order: { xs: 4, sm: 3 },
                      }}
                    />
                  </Box>

                  {/* Service Type */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTextField
                      select
                      size="small"
                      label="Service Type"
                      value={selectedServiceTypes}
                      onChange={handleServiceTypeChange}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected || selected.length === 0) {
                            return "Select Service";
                          }
                          if (selected.length === serviceTypeOptions.length) {
                            return "Selected";
                          }
                          if (selected.length === 1) {
                            return selected[0];
                          }
                          return `${selected.length} service types selected`;
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 220,
                              width: 300,
                            }
                          }
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      <MenuItem key="all" value="all">
                        <Checkbox checked={selectedServiceTypes.length === serviceTypeOptions.length} />
                        <ListItemText 
                          primary="All" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                            }
                          }}
                        />
                      </MenuItem>
                      {serviceTypeOptions.map((service) => (
                        <MenuItem key={service.value} value={service.value}>
                          <Checkbox checked={selectedServiceTypes.includes(service.value)} />
                          <ListItemText 
                            primary={service.label} 
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '0.875rem',
                              }
                            }}
                          />
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Box>

                  {/* Customer Name */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <CustomTextField
                      select
                      size="small"
                      label="Customer Name"
                      value={selectedCustomerNames}
                      onChange={handleCustomerNameChange}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected || selected.length === 0) {
                            return "Select Customer";
                          }
                          if (selected.length === customerNameOptions.length) {
                            return "Selected";
                          }
                          if (selected.length === 1) {
                            const customer = customerNameOptions.find(c => c.value === selected[0]);
                            return customer ? customer.label : selected[0];
                          }
                          return `${selected.length} customers selected`;
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 220,
                              width: 300,
                            }
                          }
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      <MenuItem key="all" value="all">
                        <Checkbox checked={selectedCustomerNames.length === customerNameOptions.length} />
                        <ListItemText 
                          primary="All" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                            }
                          }}
                        />
                      </MenuItem>
                      {customerNameOptions.map((customer) => (
                        <MenuItem key={customer.value} value={customer.value}>
                          <Checkbox checked={selectedCustomerNames.includes(customer.value)} />
                          <ListItemText 
                            primary={customer.label} 
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '0.875rem',
                              }
                            }}
                          />
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Box>

                  {/* Customer Type */}
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                     <CustomTextField
                      select
                      size="small"
                      label="Customer Type"
                      value={selectedCustomerTypes}
                      onChange={handleCustomerTypeChange}
                      SelectProps={{
                        multiple: true,
                        displayEmpty: true,
                        renderValue: (selected) => {
                          if (!selected || selected.length === 0) {
                            return "Select Type";
                          }
                          if (selected.length === customerTypeOptions.length) {
                            return "Selected";
                          }
                          if (selected.length === 1) {
                            return selected[0];
                          }
                          return `${selected.length} types selected`;
                        },
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 220,
                              width: 300,
                            }
                          }
                        }
                      }}
                      sx={{
                        width: "300px !important",
                        "& .MuiInputBase-root": {
                          width: "100% !important",
                          border: "1px solid #D9D9D9",
                          boxShadow: "none",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#1B2431"
                              : "#fff !important",
                        },
                      }}
                    >
                      <MenuItem key="all" value="all">
                        <Checkbox checked={selectedCustomerTypes.length === customerTypeOptions.length} />
                        <ListItemText 
                          primary="All" 
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                            }
                          }}
                        />
                      </MenuItem>
                      {customerTypeOptions.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Checkbox checked={selectedCustomerTypes.includes(type.value)} />
                          <ListItemText 
                            primary={type.label} 
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '0.875rem',
                              }
                            }}
                          />
                        </MenuItem>
                      ))}
                    </CustomTextField>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    flexWrap: "wrap",
                    mt: 4,
                  }}
                >
                  <Button
                    variant="outlined"
                    color="main"
                    size="small"
                    onClick={handleRunFilter}
                    disabled={dataLoading}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    {dataLoading ? 'Loading...' : 'Run Filter'}
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion 
              expanded={conversionAccordionExpanded} 
              onChange={handleConversionAccordionChange}
              sx={{ mb: 3 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ bgcolor: "#F8FAFC" }}
              >
                <Typography variant="h5">Conversion Details</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: "#F8FAFC" }}>
               <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, sm: 3 },
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                          Total Services
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            bgcolor: "rgba(35, 162, 109, 0.12)",
                            borderRadius: "50%",
                            width: 34,
                            height: 34,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon icon="la:user-cog" width={20} height={20} style={{ color: "#23A26D" }} />
                        </Box>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" color="primary" pb={5}>
                        {dataLoading ? '...' : summaryMetrics.totalServices}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, sm: 3 },
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                          Total Revenue
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            bgcolor:"rgba(0, 58, 112, 0.1)",
                            borderRadius: "50%",
                            width: 34,
                            height: 34,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon icon="mdi:cash-multiple" width={20} height={20} style={{ color: "#003A70" }} />
                        </Box>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" color="primary" pb={5}>
                        {dataLoading ? '...' : `$${summaryMetrics.totalRevenue.toLocaleString()}`}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, sm: 3 },
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                          Total Cost
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            bgcolor: "rgba(37, 99, 235, 0.1)",
                            borderRadius: "50%",
                            width: 34,
                            height: 34,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon icon="octicon:graph-16" width={20} height={20} style={{ color: "#2563EB" }} />
                        </Box>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" color="primary" pb={5}>
                        {dataLoading ? '...' : `$${summaryMetrics.totalCost.toLocaleString()}`}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: { xs: 3, sm: 3 },
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body1" color="text.secondary">
                          Avg Profit Margin
                        </Typography>
                        <Box
                          sx={{
                            display: "inline-flex",
                            bgcolor: "rgba(221, 153, 47, 0.1)",
                            borderRadius: "50%",
                            width: 34,
                            height: 34,
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Icon icon="ant-design:percentage-outlined" width={20} height={20} style={{ color: "#DD992F" }} />
                        </Box>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" color="primary" pb={5}>
                        {dataLoading ? '...' : `${summaryMetrics.avgProfitMargin.toLocaleString()}%`}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Box sx={{ height: 400, width: "100%", mt: 5 }}>
              <DataGridPro
                rows={aggregatedServicesData}
                columns={columns}
                loading={dataLoading}
                disableRowSelectionOnClick
                // pageSizeOptions={[5, 10, 50]}
                // initialState={{
                //   pagination: { paginationModel: { pageSize: 5 } },
                // }}
                slots={{
                  footer: () => null
                }}
               
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textTransform: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    textTransform: "none",
                  },
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Total Usage Dialog */}
      <Dialog
        open={usageDialogOpen}
        onClose={handleCloseDialogs}
       fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { maxHeight: "85vh" } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Total Usage Details - {dialogServiceName}</Typography>
            <IconButton onClick={handleCloseDialogs}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 250, width: "100%", mt: 2 }}>
            <DataGridPro
              rows={getUsageDialogData(dialogServiceName)}
              columns={usageColumns}
              disableRowSelectionOnClick
              // pageSizeOptions={[5, 10]}
              // initialState={{
              //   pagination: { paginationModel: { pageSize: 5 } },
              // }}
              slots={{
                footer: () => null
              }}
              
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Total Revenue Dialog */}
      <Dialog
        open={revenueDialogOpen}
        onClose={handleCloseDialogs}
        fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { maxHeight: "85vh" } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Total Revenue Details - {dialogServiceName}</Typography>
            <IconButton onClick={handleCloseDialogs}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 250, width: "100%", mt: 2 }}>
            <DataGridPro
              rows={getRevenueDialogData(dialogServiceName)}
              columns={revenueColumns}
              disableRowSelectionOnClick
              // pageSizeOptions={[5, 10]}
              // initialState={{
              //   pagination: { paginationModel: { pageSize: 5 } },
              // }}
              slots={{
                footer: () => null
              }}
             
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Total Cost Dialog */}
      <Dialog
        open={costDialogOpen}
        onClose={handleCloseDialogs}
       fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { maxHeight: "85vh" } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Total Cost Details - {dialogServiceName}</Typography>
            <IconButton onClick={handleCloseDialogs}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 250, width: "100%", mt: 2 }}>
            <DataGridPro
              rows={getCostDialogData(dialogServiceName)}
              columns={costColumns}
              disableRowSelectionOnClick
              // pageSizeOptions={[5, 10]}
              // initialState={{
              //   pagination: { paginationModel: { pageSize: 5 } },
              // }}
              slots={{
                footer: () => null
              }}
             
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Profit Dialog */}
      <Dialog
        open={profitDialogOpen}
        onClose={handleCloseDialogs}
       fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { maxHeight: "85vh" } }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">Profit Details - {dialogServiceName}</Typography>
            <IconButton onClick={handleCloseDialogs}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 250, width: "100%", mt: 2 }}>
            <DataGridPro
              rows={getProfitDialogData(dialogServiceName)}
              columns={profitColumns}
              disableRowSelectionOnClick
              // pageSizeOptions={[5, 10]}
              // initialState={{
              //   pagination: { paginationModel: { pageSize: 5 } },
              // }}
              slots={{
                footer: () => null
              }}
             
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  textTransform: "none",
                },
              }}
            />
          </Box>
          
         
         
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};

export default ServiceBundleReports;